// @mui
import { Box, Button, Stack, Alert } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { getMaintenanceRecord } from '../store/maintenanceActions';
import { getProfile } from '../../../shared/auth/authService';
import { AcceptReturn, DeclineReturn } from '.';
import MaintenanceRecordDetails from './MaintenanceRecordDetails';
import call from '../../../core/services/http';
import { errorMessage } from '../../../core/services/http/apiAction';
import maintenanceConstants from '../store/maintenanceConstants';

// Rejection reasons were captured by the API but never rendered, so neither the
// provider nor the manager could see why something had been turned down.
const NOTES = [
  { label: 'Admin decision', value: 'admin_approval_notes', severity: 'info' },
  { label: 'Service provider note', value: 'rsp_approval_notes', severity: 'info' },
  { label: 'Return decision', value: 'asset_return_approval_notes', severity: 'warning' },
];

MaintenanceDetailCard.propTypes = {
  recordId: PropTypes.string,
};

export default function MaintenanceDetailCard({ recordId }) {
  const dispatch = useDispatch();
  const { maintenanceRecord } = useSelector((state) => state.maintenanceReducer);
  const [user, setUser] = useState({});
  const [open, setOpen] = useState(false);
  const [openDecline, setOpenDecline] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const refresh = () => dispatch(getMaintenanceRecord(recordId));

  const handleClose = (saved) => {
    setOpen(false);
    if (saved) refresh();
  };
  const handleDecline = (saved) => {
    setOpenDecline(false);
    if (saved) refresh();
  };

  const downloadJobCard = async () => {
    try {
      setIsDownloading(true);
      const response = await call('get', maintenanceConstants.JOB_CARD(recordId), { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener');
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(errorMessage(error, 'We could not open the job card.'));
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    const profile = getProfile();
    if (profile) {
      setUser(profile);
    }
  }, []);
  return (
    <>
      {open && <AcceptReturn open={open} handleClose={handleClose} maintenanceId={recordId} />}
      {openDecline && <DeclineReturn open={openDecline} handleClose={handleDecline} maintenanceId={recordId} />}
      {(maintenanceRecord && (
        <>
          <Stack spacing={1}>
            <Box sx={{ p: 2 }}>
              <Stack spacing={1} direction="row" sx={{ float: 'right' }}>
                {maintenanceRecord?.job_card_number && (
                  <LoadingButton size="small" variant="outlined" loading={isDownloading} onClick={downloadJobCard}>
                    Job Card
                  </LoadingButton>
                )}
                {(user?.role === 'MANAGER' || user?.role === 'ADMIN') &&
                  maintenanceRecord?.status === 'AWAITING_MANAGER_ACCEPTANCE' && (
                    <>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => {
                          setOpen(true);
                        }}
                      >
                        Accept
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        variant="contained"
                        onClick={() => {
                          setOpenDecline(true);
                        }}
                      >
                        Reject
                      </Button>
                    </>
                  )}
              </Stack>
            </Box>
            <MaintenanceRecordDetails record={maintenanceRecord} />
            {NOTES.map(({ label, value, severity }) =>
              maintenanceRecord?.[value] ? (
                <Alert key={value} severity={severity} sx={{ mx: 3 }}>
                  <strong>{label}:</strong> {maintenanceRecord[value]}
                </Alert>
              ) : null
            )}
          </Stack>
        </>
      )) ||
        'Maintenance record not found'}
    </>
  );
}
